import React from 'react';
import {
  Button,
  Checkbox,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { formatRequestTypeLabel, isDetailsColumn, isStatusColumn, normalizeDisplayValue } from './requestsUiUtils';

export default function RequestsDesktopTable({
  loading,
  rows,
  columns,
  selectable,
  showActionsColumn,
  selectedIds,
  allSelected,
  someSelected,
  onToggleAll,
  onToggleOne,
  isRowSelectable,
  onRowClick,
  renderRowActions,
  renderStatus,
  compactActionsColumn = false,
  onEmptyAction,
  emptyActionLabel,
}) {
  const theme = useTheme();
  const colSpan = (selectable ? 1 : 0) + 1 + columns.length + (showActionsColumn ? 1 : 0);

  return (
    <TableContainer
      sx={{
        maxHeight: 680,
        overflow: 'auto',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.08),
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              '& th': {
                py: 1.35,
                fontWeight: 800,
                color: 'text.secondary',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontSize: theme.typography.pxToRem(11.5),
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderBottomColor: alpha(theme.palette.primary.main, 0.12),
              },
            }}
          >
            {selectable ? (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => onToggleAll(e.target.checked)}
                />
              </TableCell>
            ) : null}
            <TableCell>Requested By</TableCell>
            {columns.map((col, idx) => (
              <TableCell key={`${col.label}-${idx}`}>{col.label}</TableCell>
            ))}
            {showActionsColumn ? <TableCell align="right">Actions</TableCell> : null}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading
            ? Array.from({ length: 6 }).map((_, idx) => (
                <TableRow key={`skeleton-${idx}`}>
                  {selectable ? (
                    <TableCell padding="checkbox">
                      <Skeleton variant="rectangular" width={18} height={18} />
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <Skeleton width={160} />
                  </TableCell>
                  {columns.map((col, i) => (
                    <TableCell key={`sk-${col.label}-${i}`}>
                      <Skeleton width={120} />
                    </TableCell>
                  ))}
                  {showActionsColumn ? (
                    <TableCell align="right">
                      <Skeleton width={compactActionsColumn ? 96 : 150} sx={{ ml: 'auto' }} />
                    </TableCell>
                  ) : null}
                </TableRow>
              ))
            : null}

          {!loading && rows.length
            ? rows.map((req) => {
                const rowSelectable =
                  selectable && (typeof isRowSelectable === 'function' ? isRowSelectable(req) : true);

                return (
                  <TableRow
                    key={req.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 160ms ease, box-shadow 160ms ease',
                      '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.primary.main, 0.014) },
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.055) },
                    }}
                    onClick={() => onRowClick(req)}
                  >
                  {selectable ? (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        disabled={!rowSelectable}
                        checked={selectedIds.includes(req.id)}
                        onChange={(e) => onToggleOne(req.id, e.target.checked)}
                      />
                    </TableCell>
                  ) : null}

                  <TableCell sx={{ minWidth: 220 }}>
                    <Stack spacing={0.2}>
                      <Typography variant="body2" sx={{ fontWeight: 760 }}>
                        {normalizeDisplayValue(req?.user?.name)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{req?.id || '-'} · {formatRequestTypeLabel(req?.request_type)}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {columns.map((col, i) => {
                    let value = '';
                    try {
                      value = col.accessor(req);
                    } catch {
                      value = '';
                    }

                    if (isDetailsColumn(col)) {
                      const details = String(value || '').trim();
                      const shortDetails =
                        details.length > 85 ? `${details.slice(0, 85)}...` : details;
                      return (
                        <TableCell
                          key={`${col.label}-${i}`}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ maxWidth: 460 }}
                        >
                          {details ? (
                            <Tooltip title={details}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 430, color: 'text.secondary' }}>
                                {shortDetails}
                              </Typography>
                            </Tooltip>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell key={`${col.label}-${i}`} onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                        {isStatusColumn(col) ? renderStatus(value) : normalizeDisplayValue(value)}
                      </TableCell>
                    );
                  })}

                  {showActionsColumn ? (
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ minWidth: compactActionsColumn ? 180 : 320 }}
                    >
                      {renderRowActions(req)}
                    </TableCell>
                  ) : null}
                </TableRow>
                );
              })
            : null}

          {!loading && !rows.length ? (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ p: 6 }}>
                <Stack spacing={1} alignItems="center">
                  <Typography sx={{ opacity: 0.74 }}>No requests found</Typography>
                  {onEmptyAction ? (
                    <Button variant="outlined" onClick={onEmptyAction}>
                      {emptyActionLabel || 'Create a new request'}
                    </Button>
                  ) : null}
                </Stack>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
